import RBACFlag from "@/app/components/core/RBACFlag";
import { Dialog } from "@headlessui/react";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import { useCreateEMRAPI } from "@/app/hooks/api_hooks/emr/useCreateEMRAPI";
import { useV2EMRListAPI } from "@/app/hooks/api_hooks/emr/useV2EMRListAPI";
import { isActiveEMRForPet } from "@/app/utils/voiceAutoStart";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ROUTES } from "@/app/routes";
import MappedClinicAndCityPage from "@/app/routes/error_routes/MapClinicAndCityPage";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setEMRFormFields, setEMRMode } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import { Tooltip } from "react-tooltip";

const EMRTabHeader: React.FC<{
  petName: string;
  petId: string;
  customerId: string;
  content: React.ReactNode;
  petStatus?: string;
  showEMRCreateButton: boolean;
  /* Passed by <EMRTab> when a voice recording owns an EMR for this pet. Declared
     here so the call site type-checks; the header itself doesn't branch on it. */
  voiceEmrId?: string | null;
}> = ({ customerId, petId, showEMRCreateButton, petStatus }) => {
  const { setVisible, setModalData } = useModalActions("EMR_OVERDRAFT_MODAL");
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { petDetails } = routeState.state as ICustomerProfileState;
  const { execute: createDraftEMR } = useCreateEMRAPI();
  const { execute: fetchEmrList } = useV2EMRListAPI();
  const dispatch = useAppDispatch();

  const mappedClinicID = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId ?? "");
  const currentUserEmail = useAppSelector((state) => state?.auth?.user?.email);

  const { routeState: SlotRoute } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);
  const { customerBookings } = SlotRoute.state as ISlotsState;

  const fetchCustomerBookingsAPI = useUserBookingsListAPI();
  const [isVisibleMappedClinic, setIsVisibleMappedClinic] = useState<boolean>(false);

  const [isNewEMRInProgress, setIsNewEMRInProgress] = useState(false);
  const [isBookingStatusModalVisible, setIsBookingStatusModalVisible] = useState(false);

  const handelCartCreationFunction = async (selectedClinicId?: string) => {
    setIsVisibleMappedClinic(false);
    const emrList = await fetchEmrList(customerId, petId);
    const openDraft = Array.isArray(emrList)
      ? emrList.filter((emr) => isActiveEMRForPet(emr, petId))[0]
      : undefined;

    if (openDraft?.emr_id) {
      if (currentUserEmail && openDraft.created_by === currentUserEmail) {
        window.open(
          `/app/customer/profile/create-emr?emr_id=${openDraft.emr_id}&emr_status=${
            openDraft.emr_status ?? "DRAFT"
          }`,
          "_blank",
        );
        return;
      }

      setModalData({
        createdAt: openDraft.created_at,
        createdBy: openDraft.created_by,
        emrId: openDraft.emr_id,
        cartId: openDraft.cart_id,
        customerDetails: openDraft.customer_profile_section,
        status: openDraft.emr_status,
      });
      setVisible();
      return;
    }
    setIsNewEMRInProgress(true);

    dispatch(setEMRMode("CREATE"));
    dispatch(setEMRFormFields({ formFields: null }));
    await createDraftEMR(customerId, petId, selectedClinicId).then((response) => {
      setIsNewEMRInProgress(false);
      if (response) {
        window.open(`${"/app/customer/profile/create-emr"}?emr_id=${response?.emr_id}&emr_status=DRAFT`, "_blank");
      }
      if (response?.show_booking_status_modal) setIsBookingStatusModalVisible(true);
    });
  };

  const today = new Date().toISOString().split("T")[0]; // e.g. "2025-09-08"
  const isBookingPresent = customerBookings?.data?.filter(
    (b) => b.booking_start_date === today && !["Cancelled", "Failed"].includes(b.booking_status),
  );
  const isMappedClinicPresent = isBookingPresent?.some((booking) => booking?.clinic_id === mappedClinicID);

  const handleCreateEMR = () => {
    // nevigate("/app/customer/profile/create-emr");
    if (isMappedClinicPresent) {
      handelCartCreationFunction();
    } else {
      setIsVisibleMappedClinic(true);
    }
  };

  useEffect(() => {
    if (customerId && petId) {
      fetchCustomerBookingsAPI(customerId, petId, {
        limit: 10,
        page: 1,
        append: false,
        filter_by: "all",
      });
    }
  }, []);

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-6 lg:gap-8">
      <div className="flex-1">
        <h1 className="font-semibold text-theme-xs text-gray-900 leading-6 mt-2">
          EMR Details For "{petDetails?.name}"
        </h1>
        <p className="text-gray-700 text-theme-xs">
          A list of EMR associated with the current customer.
        </p>
      </div>
      {showEMRCreateButton && (
        <div>
          <RBACFlag actionId="EDIT_EMR_BUTTON">
            <button
              data-tooltip-id="create_emr_button"
              disabled={petStatus === "Deceased" || isNewEMRInProgress}
              data-tooltip-content={petStatus === "Deceased" ? "Cannot create EMR for deceased pet" : "EMR"}
              onClick={handleCreateEMR}
              type="button"
              className="bg-[#000000] disabled:bg-gray-200 shadow-sm px-3 md:px-4 lg:px-5
py-1 md:py-2 rounded-md  font-semibold text-theme-sm text-white disabled:text-gray-700 disabled:cursor-not-allowed"
            >
              {isNewEMRInProgress ? (
                <span className="flex justify-start items-center gap-x-3 text-theme-sm">
                  {" "}
                  <FiLoader className="animate-spin" /> Creating...{" "}
                </span>
              ) : (
                "Create EMR"
              )}
            </button>
          </RBACFlag>
          {petStatus === "Deceased" && <Tooltip id="create_emr_button" style={{ fontSize: "10px" }} />}
        </div>
      )}

      {isVisibleMappedClinic && (
        <MappedClinicAndCityPage
          isCrossVisible={true}
          message="Please verify your mapped clinic. No bookings were found for this user today."
          isCartEmrCreate={true}
          enforceBookingClinic={true}
          setIsVisibleMappedClinic={setIsVisibleMappedClinic}
          handelCartEmrCreate={(clinicId) => handelCartCreationFunction(clinicId)}
        />
      )}

      <Dialog
        open={isBookingStatusModalVisible}
        onClose={() => setIsBookingStatusModalVisible(false)}
        className="relative z-40"
      >
        <div className="fixed inset-0 bg-gray-800 opacity-75" aria-hidden="true" />
        <div className="fixed inset-0 flex justify-center items-center p-4">
          <Dialog.Panel className="bg-white p-6 rounded-lg w-full max-w-md">
            <Dialog.Title className="font-semibold text-gray-900 text-sm">Cannot Create EMR</Dialog.Title>
            <p className="mt-2 text-gray-700 text-theme-xs">
            Please ask the Reception to mark this appointment as <b className="font-bold">Attending</b> before creating the EMR.
            </p>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setIsBookingStatusModalVisible(false)}
                className="bg-[#000000] px-4 py-2 rounded-md font-semibold text-theme-sm text-white"
              >
                Okay
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default EMRTabHeader;
